import type { QueryClient } from '@tanstack/react-query';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  createSavedFilterView,
  deleteSavedFilterView,
  fetchSavedFilterView,
  fetchSavedFilterViews,
  updateSavedFilterView,
} from '@/api/savedFilterViews';
import { logger } from '@/lib/logger';
import type {
  CreateSavedFilterViewRequest,
  SavedFilterView,
  SavedFilterViewWithWarnings,
  UpdateSavedFilterViewRequest,
} from '@/types/savedFilterView';

// Views change rarely; explicit save invalidates.
const SAVED_VIEWS_STALE_MS = 15 * 60 * 1000;

export const savedFilterViewsKeys = {
  all: (orgId: number) => ['savedFilterViews', orgId] as const,
  list: (orgId: number, entityType: string) =>
    ['savedFilterViews', orgId, entityType] as const,
  detail: (orgId: number, viewId: string) =>
    ['savedFilterView', orgId, viewId] as const,
};

export function useSavedFilterViewsList(
  orgId: number | undefined,
  entityType: string | undefined,
  options?: { enabled?: boolean }
) {
  return useQuery({
    queryKey: savedFilterViewsKeys.list(orgId ?? 0, entityType ?? ''),
    queryFn: ({ signal }) =>
      fetchSavedFilterViews(orgId as number, entityType as string, signal),
    enabled: !!orgId && !!entityType && (options?.enabled ?? true),
    staleTime: SAVED_VIEWS_STALE_MS,
  });
}

export function useCreateSavedFilterView(orgId: number, entityType: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateSavedFilterViewRequest) =>
      createSavedFilterView(orgId, body),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: savedFilterViewsKeys.list(orgId, entityType),
      });
    },
  });
}

/**
 * Mutation hook for PATCHing a saved view (rename, toggle share, or the
 * explicit save of filter/view-state changes). Always invalidates the
 * per-view detail cache. The updated view from the response is written into
 * the per-entity-type list cache with setQueryData — the list is the dirty
 * baseline for saved-view change tracking, so an explicit save must flip the
 * view to 'clean' synchronously with the response, without a list refetch.
 * The list is additionally invalidated (refetched) only when fields that
 * affect the list display change (name, is_shared_with_org).
 */
export function useUpdateSavedFilterView(orgId: number, entityType: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      viewId,
      body,
    }: {
      viewId: string;
      body: UpdateSavedFilterViewRequest;
    }) => updateSavedFilterView(orgId, viewId, body),
    onSuccess: (data: SavedFilterView, variables) => {
      queryClient.setQueryData<SavedFilterView[]>(
        savedFilterViewsKeys.list(orgId, entityType),
        current =>
          current?.map(view =>
            view.entity_id === variables.viewId ? data : view
          )
      );
      const affectsList =
        variables.body.name !== undefined ||
        variables.body.is_shared_with_org !== undefined;
      if (affectsList) {
        queryClient.invalidateQueries({
          queryKey: savedFilterViewsKeys.list(orgId, entityType),
        });
      }
      queryClient.invalidateQueries({
        queryKey: savedFilterViewsKeys.detail(orgId, variables.viewId),
      });
    },
    onError: (error: Error, variables) => {
      logger.warn('Saved view update failed', {
        component: 'saved-views',
        org_id: orgId,
        saved_view_id: variables.viewId,
        error_type: error.name,
        error_message: error.message,
      });
    },
  });
}

/**
 * Mutation hook for DELETing a saved view.
 * On success, invalidates the per-entity-type list cache AND removes the
 * per-view detail cache (the entity no longer exists; leaving a cached
 * detail entry would cause 404s on subsequent reads).
 */
export function useDeleteSavedFilterView(orgId: number, entityType: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (viewId: string) => deleteSavedFilterView(orgId, viewId),
    onSuccess: (_data, viewId) => {
      queryClient.invalidateQueries({
        queryKey: savedFilterViewsKeys.list(orgId, entityType),
      });
      queryClient.removeQueries({
        queryKey: savedFilterViewsKeys.detail(orgId, viewId),
      });
    },
    onError: (error: Error, viewId) => {
      logger.warn('Saved view delete failed', {
        component: 'saved-views',
        org_id: orgId,
        saved_view_id: viewId,
        error_type: error.name,
        error_message: error.message,
      });
    },
  });
}

export function fetchSavedFilterViewForApply(
  queryClient: QueryClient,
  orgId: number,
  viewId: string,
  signal?: AbortSignal
): Promise<SavedFilterViewWithWarnings> {
  return queryClient.fetchQuery({
    queryKey: savedFilterViewsKeys.detail(orgId, viewId),
    queryFn: () => fetchSavedFilterView(orgId, viewId, signal),
    staleTime: 0, // Always re-fetch on click; warnings are point-in-time.
  });
}
